import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import SSOProvider from "./components/SSOProvider.js";
import UnsavedChangesDialog from "./components/UnsavedChangesDialog";

import {
  HANDLED_ERROR_CODES,
  HTTP_STATUS_CODE,
} from "./constants/apiResponseCode.constants";
import { APP_ROUTES } from "./constants/global.constants";

import { saveItemSession } from "./utils/session";

const ERROR_ROUTES = new Set([
  `/${APP_ROUTES.UNAUTHORIZED}`,
  `/${APP_ROUTES.MAINTENANCE}`,
]);

export const RootLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const globalApiErrorCode = useSelector((state) => state.globalApiErrorCode);

  // Store the last visited URL in session storage, unless it contains an OAuth code parameter
  useEffect(() => {
    const currentUrl = `${location.pathname}${location.search}`;
    if (!currentUrl.includes("code=") && !ERROR_ROUTES.has(location.pathname)) {
      saveItemSession("lastVisitedURL", currentUrl);
    }
  }, [location.pathname, location.search]);

  // Redirect to relevant error page if API error code is set
  useEffect(() => {
    if (
      globalApiErrorCode == null ||
      !HANDLED_ERROR_CODES.includes(globalApiErrorCode)
    ) {
      return;
    }

    let apiErrorRedirectPath = null;

    switch (globalApiErrorCode) {
      case HTTP_STATUS_CODE.UNAUTHORIZED: {
        apiErrorRedirectPath = APP_ROUTES.UNAUTHORIZED;
        break;
      }
      case HTTP_STATUS_CODE.SERVICE_UNAVAILABLE: {
        apiErrorRedirectPath = APP_ROUTES.MAINTENANCE;
        break;
      }
      default:
        break;
    }

    if (!apiErrorRedirectPath || location.pathname === apiErrorRedirectPath) {
      return;
    }

    // Replace history so Back does not return to the failing page.
    navigate(`/${apiErrorRedirectPath}`, { replace: true });
  }, [globalApiErrorCode, location.pathname, navigate]);

  return (
    <SSOProvider
      clientId={process.env.REACT_APP_CONSUMER_KEY}
      redirectUri={process.env.REACT_APP_REDIRECT_URI}
    >
      <Outlet />
      <UnsavedChangesDialog />
    </SSOProvider>
  );
};
